import React, { useState, useEffect } from "react";
import { Button } from "@mui/material";
import DataTable from "../ui/DataTable";
import { Box } from "@material-ui/core";
import CheckHistoryModal from "./CheckHistoryModal";
import { Num, Pill } from "../ui";
import DateRangeFilter from "../ui/DateRangeFilter";
import moment from "moment";
import { checkStateStyles } from "../../constants/statusStyles";

/** Los estados llegan ya traducidos a texto, así que el mapa va por etiqueta. */
const CHECK_LABEL_STYLES = Object.values(checkStateStyles).reduce(
  (acc, style) => ({ ...acc, [style.label]: style }),
  {}
);

const ChecksReportTable = ({ checks }) => {
  const [displayedChecks, setDisplayedChecks] = useState(checks);
  const [search, setSearch] = useState("");
  const [stateFilter, setStateFilter] = useState(null);
  const [emision, setEmision] = useState({ from: null, to: null });
  const [cobro, setCobro] = useState({ from: null, to: null });
  const [historyModal, setHistoryModal] = useState({
    open: false,
    paymentDetailsId: null,
    checkNumber: null,
  });

  const openHistory = (check) => {
    setHistoryModal({
      open: true,
      paymentDetailsId: check?.paymentDetailsIdid ?? null,
      checkNumber: check?.checkNumber ?? null,
    });
  };

  const closeHistory = () => {
    setHistoryModal((prev) => ({ ...prev, open: false }));
  };

  // Actualizar displayedChecks cuando checks cambie
  useEffect(() => {
    setDisplayedChecks(checks);
  }, [checks]);

  const calculateTotal = (checksToSum) => {
    return checksToSum.reduce((total, check) => {
      const amount = check.checkAmount.replace('$', '').replace(/\./g, '').replace(',', '.').trim();
      return total + (parseFloat(amount) || 0);
    }, 0);
  };

  const options = {
    responsive: "simple",
    selectableRows: "none",
    sortOrder: { name: "checkEmitionDate", direction: "desc" },
    enableNestedDataAccess: ".",
    rowsPerPage: 25,
    downloadOptions: {
      filename: "cheques.csv",
      separator: ";",
      filterOptions: { useDisplayedColumnsOnly: true, useDisplayedRowsOnly: true },
    },
    onFilterChange: (column, filterList, type) => {
      let filteredData = checks;
      filterList.forEach((filters, index) => {
        if (filters.length) {
          filteredData = filteredData.filter(item => 
            filters.includes(item[columns[index].name])
          );
        }
      });
      setDisplayedChecks(filteredData);
    },
    onSearchChange: (searchText) => {
      if (searchText === null) {
        setDisplayedChecks(checks);
      } else {
        const filteredData = checks.filter(check => 
          Object.values(check).some(value => 
            value?.toString().toLowerCase().includes(searchText.toLowerCase())
          )
        );
        setDisplayedChecks(filteredData);
      }
    },
  };

  const columns = [
    {
      name: "checkEmitionDate",
      label: "F. Emisión",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "checkPaymentDate",
      label: "F. Pago",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "checkNumber",
      label: "N° Cheque",
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: "checkBank",
      label: "Banco",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "checkIssuerName",
      label: "Firmante",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "checkState",
      label: "Estado",
      options: {
        filter: true,
        sort: true,
        // checksScreen ya mapea 0/1/2 a su etiqueta; acá sólo le ponemos color.
        customBodyRender: (value) => {
          const style = CHECK_LABEL_STYLES[value];
          if (!style) return value;
          return <Pill label={value} color={style.text} background={style.bg} />;
        },
      },
    },
    {
      name: "checkAmount",
      label: "Monto",
      options: {
        filter: true,
        sort: true,
        setCellProps: () => ({ style: { textAlign: "right" } }),
        setCellHeaderProps: () => ({ style: { textAlign: "right" } }),
        customBodyRender: (value) => <Num sx={{ fontSize: "13px" }}>{value}</Num>,
      },
    },
    {
      name: "historial",
      label: "Historial",
      options: {
        filter: false,
        sort: false,
        empty: true,
        customBodyRenderLite: (dataIndex) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => openHistory(visibleChecks[dataIndex])}
          >
            Ver historial
          </Button>
        ),
      },
    },
  ];

  const term = search.trim().toLowerCase();

  /** Las fechas llegan ya formateadas como "YYYY-MM-DD" desde checksScreen. */
  const inRange = (value, range) => {
    if (!range.from && !range.to) return true;
    if (!value) return false;
    const date = moment(value, "YYYY-MM-DD");
    if (!date.isValid()) return false;
    if (range.from && date.isBefore(moment(range.from), "day")) return false;
    if (range.to && date.isAfter(moment(range.to), "day")) return false;
    return true;
  };

  const visibleChecks = displayedChecks.filter((check) => {
    if (stateFilter && check.checkState !== stateFilter) return false;
    if (!inRange(check.checkEmitionDate, emision)) return false;
    if (!inRange(check.checkPaymentDate, cobro)) return false;
    if (!term) return true;
    return Object.values(check || {}).some(
      (value) =>
        value !== null &&
        typeof value !== "object" &&
        String(value).toLowerCase().includes(term)
    );
  });

  const stateChips = [
    {
      id: "todos",
      label: "Todos",
      active: stateFilter === null,
      onClick: () => setStateFilter(null),
    },
    ...Object.values(checkStateStyles).map((style) => ({
      id: style.label,
      label: style.label,
      dot: style.dot,
      active: stateFilter === style.label,
      onClick: () => setStateFilter(stateFilter === style.label ? null : style.label),
    })),
  ];

  return (
    <>
      <DataTable
        data={visibleChecks}
        columns={columns}
        heading="Reporte de cheques"
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Buscar por número, banco o librador…",
        }}
        chips={stateChips}
        filters={
          <>
            <DateRangeFilter
              from={emision.from}
              to={emision.to}
              label="Emisión"
              onChange={(from, to) => setEmision({ from, to })}
            />
            <DateRangeFilter
              from={cobro.from}
              to={cobro.to}
              label="Cobro"
              onChange={(from, to) => setCobro({ from, to })}
            />
          </>
        }
        resultCount={visibleChecks.length}
        totalCount={checks.length}
        emptyMessage="No hay cheques que coincidan"
        options={options}
      />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: "10px",
          padding: "14px 18px",
          marginTop: "12px",
          backgroundColor: "var(--sup-surface)",
          border: "1px solid var(--sup-border)",
          borderRadius: "12px",
          fontWeight: 600,
        }}
      >
        <h4>TOTAL: $ {calculateTotal(displayedChecks).toLocaleString('es-AR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: true
        })}</h4>
      </Box>
      <CheckHistoryModal
        open={historyModal.open}
        onClose={closeHistory}
        paymentDetailsId={historyModal.paymentDetailsId}
        checkNumber={historyModal.checkNumber}
      />
    </>
  );
};

export default ChecksReportTable; 